/* eslint-disable @typescript-eslint/consistent-type-imports */
import React from 'react';
import { useIntl } from 'react-intl';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';
import type { ExtensionConfiguration } from '@atlaskit/editor-plugin-selection-extension/types';

import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	setInEditorTemplatesPanelQueryParam,
	IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
	CONTEXT_PANEL_TAB_TARGET_BLOCK,
} from '@confluence/template-utils/entry-points/urlUtils.ts';

import { useHandleCreateSnippet } from '../hooks/modals/useHandleCreateSnippet.ts';
import { useIsSnippetCreateEnabled } from '../hooks/embedded-confluence/useIsSnippetCreateEnabled.ts';

import { buildSnippetExtension } from './buildSnippetExtension.ts';

export type UseSnippetExtensionProps = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPIRef?: React.RefObject<PublicPluginAPI<[QuickInsertPlugin]> | null | undefined>;
};

/**
 * Returns the snippet extension list for the selection extension (create block template).
 * Pass editorAPIRef from the caller (e.g. a ref holding fullPageEditorPresetAPI from useFullPageEditorApi).
 */
export const useSnippetExtension = ({
	contentId,
	spaceId,
	spaceKey,
	editorAPIRef,
}: UseSnippetExtensionProps): ExtensionConfiguration[] => {
	const { formatMessage } = useIntl();
	const { setQueryParams } = useRouteActions();
	const isCreateEnabled = useIsSnippetCreateEnabled();
	const handleCreateSnippet = useHandleCreateSnippet({
		contentId,
		spaceId,
		spaceKey,
		editorAPIRef,
	});
	const handleOpenTemplatePanel = () => {
		setInEditorTemplatesPanelQueryParam(
			setQueryParams,
			IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
			CONTEXT_PANEL_TAB_TARGET_BLOCK,
		);
	};

	if (!isCreateEnabled) {
		return [];
	}

	return buildSnippetExtension({
		formatMessage,
		onCreateSnippet: handleCreateSnippet,
		onOpenTemplatePanel: handleOpenTemplatePanel,
	});
};
